import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { NDDTranslationService } from '@ndk/ng-translation';

import { INDDFormsCustomValidationErrors, INDDFormsCustomValidatorFn } from '@shared/ndd-ng-forms/ndd-ng-form-error/shared/ndd-ng-form-error.model';

/** Serviço de validadores personalizados. */
@Injectable()
export class CustomValidatorsService {
    private readonly patternDotsAndSpacesForFolder: RegExp = /(?!\s+|\.+|\s+\.+|\.+\s+).+/;
    private readonly patternReservedFileCharacters: RegExp = /^[^<>:"/\\|?*]+$/;
    private readonly patternReservedNames: RegExp = /(?:.*\\\s+|\\|^\s*)(?:CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:\s+\.|\.|\s+\\|\\|\s+$|$)/i;
    private readonly patternSharepointReservedPathCharacters: RegExp = /^[^<>:"\\|?*]+$/;
    private readonly patternSharepointReservedNames: RegExp = /(?:.*\/\s+|\/|^\s*)(?:FORMS|CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:\s+\.|\.|\s+\/|\/|\s+$|$)/i;
    private readonly patternEndWithPeriod: RegExp = /(?:\.+|\.\s+)$/;

    constructor(private translationService: NDDTranslationService) { }

    /** Verifica se logon é válido. */
    public logon(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value && control.value.includes('\\')) {
            return { customMessage: '360InvalidLogon' };
        }

        return null;
    }

    /**
     * Verifica se senha e confirmação de senha condizem.
     * Validação feita de forma manual (com `setErrors({...})`) para evitar loops (ocorrem com uso de `updateValueAndValidity()`).
     *
     * @param {string} comparisonControlName Nome do controle para comparação do valor com o controle atual.
     * @param {string} comparisonInvalidMessage Mensagem para indicar o campo inválido.
     */
    public confirmPassword(comparisonControlName: string, comparisonInvalidMessage: string): INDDFormsCustomValidatorFn {
        return (currentControl: AbstractControl): INDDFormsCustomValidationErrors => {
            const comparisonControl: AbstractControl = currentControl.parent.get(comparisonControlName);
            if (currentControl && currentControl.value && comparisonControl && comparisonControl.value) {
                if (currentControl.value !== comparisonControl.value) {
                    currentControl.setErrors({ customMessage: comparisonInvalidMessage });
                    comparisonControl.setErrors({ customMessage: comparisonInvalidMessage });

                    return null;
                }

                currentControl.setErrors(null);
                comparisonControl.setErrors(null);
            }

            return null;
        };
    }

    /**
     * Verifica se o texto é uma URL válida
     */
    public matchUrl(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value) {
            try {
                new URL(control.value);
            } catch {
                return { customMessage: '360InvalidPattern' };
            }
        }

        return null;
    }

    /**
     * Verifica se o nome de arquivo é válido para o Windows.
     */
    public windowsFileName(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value) {
            if (this.hasReservedFileCharacters(control.value) || this.hasReservedNames(control.value)) {
                return { customMessage: '360ReservedValues' };
            }

            if (this.endWithPeriod(control.value)) {
                return { customMessage: '360InvalidPattern' };
            }
        }

        return null;
    }

    /**
     * Verifica se a url do sharepoint é de administração (`-admin.sharepoint`)
     */
    public sharepointAdminUrl(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value) {
            if (control.value.toLowerCase().includes('-admin.sharepoint')) {
                return { customMessage: '360SharePointUrlAdmin' };
            }
        }

        return null;
    }

    /**
     * Verifica se a url do sharepoint contém complemento
     */
    public sharepointUrlComplement(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value) {
            try {
                const pathname: string = new URL(control.value).pathname;
                if (pathname !== '' && pathname !== '/') {
                    return { customMessage: this.translationService.instant('360UrlInvalidComplement', [pathname]) };
                }
            } catch {
                return { customMessage: '360InvalidPattern' };
            }
        }

        return null;
    }

    /**
     * Verifica se o nome da pasta do sharepoint é válido.
     */
    public sharepointPathName(control: AbstractControl): INDDFormsCustomValidationErrors {
        if (control.value) {
            if (this.hasSharepointReservedPathCharacters(control.value) || this.hasSharepointReservedNames(control.value)) {
                return { customMessage: '360ReservedValues' };
            }

            if (this.foldersHaveOnlyDotsAndSpaces(control.value)) {
                return { customMessage: '360InvalidPattern' };
            }
        }

        return null;
    }

    /** Verifica se o valor contém caracteres reservados. */
    private hasReservedFileCharacters(value: string): boolean {
        return !this.patternReservedFileCharacters.test(value);
    }

    /**
     * Verifica se o valor não inicia com barra '/',
     * se não possui apenas espaço(s) ou ponto(s) entre duas barras e se possui mais de uma barra como separador.
     */
    private foldersHaveOnlyDotsAndSpaces(value: string): boolean {
        const folders: string[] = value.split('/');

        return folders.some((folder: string) =>
            !folder.match(this.patternDotsAndSpacesForFolder) || folder.match(this.patternEndWithPeriod) || folder.length === 0);
    }

    /**
     * Verifica se o valor contém os nomes reservados, incluindo ou não "extensões" (a validação ocorre para nomes de pastas também):
     * CON, PRN, AUX, NUL, COM1, COM2, COM3, COM4, COM5, COM6, COM7, COM8, COM9, LPT1, LPT2, LPT3, LPT4, LPT5, LPT6, LPT7, LPT8, e LPT9.
     */
    private hasReservedNames(value: string): boolean {
        return this.patternReservedNames.test(value);
    }

    /** Verifica se o valor termina com ponto. */
    private endWithPeriod(value: string): boolean {
        return this.patternEndWithPeriod.test(value);
    }

    /**
     * Verifica se o valor contém os nomes reservados, incluindo ou não "extensões" (a validação ocorre para nomes de pastas também):
     * FORMS, CON, PRN, AUX, NUL, COM1, COM2, COM3, COM4, COM5, COM6, COM7, COM8, COM9, LPT1, LPT2, LPT3, LPT4, LPT5, LPT6, LPT7, LPT8, e LPT9.
   */
    private hasSharepointReservedNames(value: string): boolean {
        return this.patternSharepointReservedNames.test(value);
    }

    /** Verifica se o valor contém caracteres reservados das pastas do sharepoint. */
    private hasSharepointReservedPathCharacters(value: string): boolean {
        return !this.patternSharepointReservedPathCharacters.test(value);
    }
}
